/**
 * AI Settings Page Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Main admin page for AI configuration with tabbed interface.
 * Includes feature toggles, PII masking, compliance settings,
 * topic restrictions, and usage statistics.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, AlertTriangle, Shield, Bot } from 'lucide-react';
import { aiSettingsService } from '../../services/admin';
import { logger } from '../../utils/logger';
import type { AISettings, AISettingsUpdateData, TopicRestriction } from '../../types/admin';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';
import {
  AIFeatureToggles,
  AIDataAccessSettings,
  AIPIIMaskingSettings,
  AIComplianceSettings,
  AIBehaviorSettings,
  AITopicRestrictions,
  AIRateLimitSettings,
  AILoggingSettings,
  AIUsageStats,
} from '../../components/admin/ai';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

type TabId =
  | 'features'
  | 'data'
  | 'pii'
  | 'compliance'
  | 'behavior'
  | 'topics'
  | 'limits'
  | 'logging'
  | 'usage';

const TABS: { id: TabId; label: string }[] = [
  { id: 'features', label: 'Features' },
  { id: 'data', label: 'Data Access' },
  { id: 'pii', label: 'PII Masking' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'behavior', label: 'Behavior' },
  { id: 'topics', label: 'Topics' },
  { id: 'limits', label: 'Rate Limits' },
  { id: 'logging', label: 'Logging' },
  { id: 'usage', label: 'Usage Stats' },
];

/**
 * Map AISettings to form data for editing.
 * Extracted to avoid duplication between loadData and handleResetToDefaults.
 */
const mapSettingsToFormData = (settings: AISettings): AISettingsUpdateData => ({
  ai_enabled: settings.ai_enabled,
  ai_chat_enabled: settings.ai_chat_enabled,
  ai_meeting_assistant_enabled: settings.ai_meeting_assistant_enabled,
  ai_document_analysis_enabled: settings.ai_document_analysis_enabled,
  ai_form_prefill_enabled: settings.ai_form_prefill_enabled,
  ai_brief_generation_enabled: settings.ai_brief_generation_enabled,
  ai_can_access_client_data: settings.ai_can_access_client_data,
  ai_can_access_portfolio_data: settings.ai_can_access_portfolio_data,
  ai_can_access_meeting_transcripts: settings.ai_can_access_meeting_transcripts,
  ai_can_access_documents: settings.ai_can_access_documents,
  ai_sensitive_data_access: settings.ai_sensitive_data_access,
  mask_personal_identifiers: settings.mask_personal_identifiers,
  mask_financial_data: settings.mask_financial_data,
  mask_contact_info: settings.mask_contact_info,
  mask_demographic_data: settings.mask_demographic_data,
  pii_masking_mode: settings.pii_masking_mode,
  investment_advice_allowed: settings.investment_advice_allowed,
  tax_advice_allowed: settings.tax_advice_allowed,
  legal_advice_allowed: settings.legal_advice_allowed,
  require_disclaimer: settings.require_disclaimer,
  disclaimer_text: settings.disclaimer_text,
  eu_ai_act_compliant: settings.eu_ai_act_compliant,
  gdpr_compliant: settings.gdpr_compliant,
  scan_output_for_pii: settings.scan_output_for_pii,
  block_pii_in_output: settings.block_pii_in_output,
  pii_detection_sensitivity: settings.pii_detection_sensitivity,
  ai_personality: settings.ai_personality,
  ai_language: settings.ai_language,
  custom_instructions: settings.custom_instructions,
  max_requests_per_user_per_hour: settings.max_requests_per_user_per_hour,
  max_requests_per_account_per_day: settings.max_requests_per_account_per_day,
  log_ai_interactions: settings.log_ai_interactions,
  log_retention_days: settings.log_retention_days,
  anonymize_logs: settings.anonymize_logs,
  log_compliance_flags: settings.log_compliance_flags,
});

const AISettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AISettings | null>(null);
  const [restrictions, setRestrictions] = useState<TopicRestriction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('features');
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Form state for updates
  const [formData, setFormData] = useState<AISettingsUpdateData>({});

  // Load settings and restrictions
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [settingsData, restrictionsData] = await Promise.all([
        aiSettingsService.getAISettings(),
        aiSettingsService.getTopicRestrictions(),
      ]);
      setSettings(settingsData);
      setRestrictions(restrictionsData);
      setFormData(mapSettingsToFormData(settingsData));
      setHasChanges(false);
    } catch (error) {
      logger.error('Failed to load AI settings:', error);
      toast.error('Failed to load AI settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle form data changes
  const handleChange = useCallback((updates: Partial<AISettingsUpdateData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  }, []);

  // Save settings
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await aiSettingsService.updateAISettings(formData);
      setSettings(updated);
      setHasChanges(false);
      toast.success('AI settings saved successfully');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save AI settings'));
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to defaults
  const handleResetToDefaults = async () => {
    setIsSaving(true);
    try {
      const updated = await aiSettingsService.resetAISettingsToDefaults();
      setSettings(updated);
      setFormData(mapSettingsToFormData(updated));
      setHasChanges(false);
      toast.success('AI settings reset to defaults');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to reset settings'));
    } finally {
      setIsSaving(false);
      setShowResetConfirm(false);
    }
  };

  // Reload restrictions after add/remove
  const handleRestrictionsChange = useCallback(async () => {
    try {
      const data = await aiSettingsService.getTopicRestrictions();
      setRestrictions(data);
    } catch (error) {
      logger.error('Failed to reload restrictions:', error);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full" />
      </div>
    );
  }

  // Get compliance status
  const complianceStatus = settings ? aiSettingsService.getComplianceStatus(settings) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
            <Bot size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Assistant Settings</h1>
            <p className="text-gray-500 mt-0.5">
              Configure AI features, data access, and compliance settings
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowResetConfirm(true)}
            disabled={isSaving}
          >
            <RefreshCw size={16} className="mr-2" />
            Reset Defaults
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            <Save size={16} className="mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Compliance Notice */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-medium text-blue-900">EU AI Act & GDPR Compliance</h3>
            <p className="text-sm text-blue-700 mt-1">
              Default settings are configured for EU AI Act and GDPR compliance. Changes may affect
              your compliance status. Investment, tax, and legal advice are disabled by default to
              meet regulatory requirements.
            </p>
            {complianceStatus && complianceStatus.issues.length > 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle size={16} />
                  <span className="font-medium">Compliance Warnings:</span>
                </div>
                <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                  {complianceStatus.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors
                ${
                  activeTab === tab.id
                    ? 'bg-white border border-b-0 border-gray-200 text-[#002B45]'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {activeTab === 'features' && (
          <AIFeatureToggles
            formData={formData}
            onChange={handleChange}
          />
        )}
        {activeTab === 'data' && (
          <AIDataAccessSettings
            formData={formData}
            onChange={handleChange}
          />
        )}
        {activeTab === 'pii' && (
          <AIPIIMaskingSettings
            formData={formData}
            onChange={handleChange}
          />
        )}
        {activeTab === 'compliance' && (
          <AIComplianceSettings
            formData={formData}
            onChange={handleChange}
          />
        )}
        {activeTab === 'behavior' && (
          <AIBehaviorSettings
            formData={formData}
            onChange={handleChange}
          />
        )}
        {activeTab === 'topics' && (
          <AITopicRestrictions
            restrictions={restrictions}
            onRestrictionsChange={handleRestrictionsChange}
          />
        )}
        {activeTab === 'limits' && (
          <AIRateLimitSettings
            formData={formData}
            onChange={handleChange}
          />
        )}
        {activeTab === 'logging' && (
          <AILoggingSettings
            formData={formData}
            onChange={handleChange}
          />
        )}
        {activeTab === 'usage' && <AIUsageStats />}
      </div>

      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        open={showResetConfirm}
        onOpenChange={setShowResetConfirm}
        onConfirm={handleResetToDefaults}
        title="Reset to Defaults?"
        description="This will reset all AI settings to EU AI Act and GDPR compliant defaults. This action cannot be undone."
        confirmText="Reset to Defaults"
        isLoading={isSaving}
      />
    </div>
  );
};

export default AISettingsPage;
